import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Chip, CircularProgress, TextField } from "@mui/material";
import { toast } from "react-toastify";
import AttachmentList from "./AttachmentList";
import AttachmentPicker from "./AttachmentPicker";
import StatusChangeDialog from "./StatusChangeDialog";
import TicketConversation from "./TicketConversation";
import TicketStatusHistory from "./TicketStatusHistory";
import {
  pasteTicketFiles,
  statusActions,
  statusColor,
  statusLabel,
  ticketFormData,
} from "./supportTicketStatus";

const when = (value) => (value ? new Date(value).toLocaleString() : "");
const errorText = (error, fallback) =>
  error?.response?.data?.errorMessage || error?.response?.data?.message || fallback;

/**
 * One support ticket as a conversation. Shared by the portal and the office: the caller passes its
 * own API functions and which side it is, and the rules come from the server either way.
 *
 * Only an open ticket takes replies. A closed one shows why the box is disabled and offers to reopen
 * it when the viewer may; a ticket the office locked can only be reopened by the office.
 */
const SupportTicketThread = ({ ticketId, api, keyBase, viewerSide, canWrite = true, onRead }) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState([]);
  const [action, setAction] = useState(null);

  const detailKey = [...keyBase, "detail", ticketId];
  const entriesKey = [...keyBase, "entries", ticketId];

  const detail = useQuery(detailKey, () => api.fetchDetail(ticketId), { refetchOnMount: true });

  const markRead = useMutation(() => api.markRead(ticketId), { onSuccess: () => onRead?.() });
  useEffect(() => {
    if (detail.data) markRead.mutate();
    // Mark read once per load of the ticket, and again whenever new activity arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data?.lastActivityAt]);

  // The admin overview badges count open tickets: a reply or status move changes that number.
  // A no-op when the viewer never fetched the overview (e.g. the partner portal).
  const refresh = () => {
    queryClient.invalidateQueries(keyBase);
    queryClient.invalidateQueries(["referral-overview"]);
  };

  const reply = useMutation(
    () => api.reply(ticketId, ticketFormData({ body: draft.trim() }, files)),
    {
      onSuccess: () => {
        setDraft("");
        setFiles([]);
        refresh();
      },
      onError: (error) => toast.error(errorText(error, "Could not send that reply.")),
    }
  );

  const changeStatus = useMutation(
    ({ to, note }) => api.changeStatus(ticketId, { status: to, note: note || null }),
    {
      onSuccess: (_, { to }) => {
        setAction(null);
        toast.success(`Ticket ${statusLabel(to).toLowerCase()}`);
        refresh();
      },
      onError: (error) => toast.error(errorText(error, "Could not change the ticket status.")),
    }
  );

  if (detail.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <CircularProgress size={24} />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Alert severity="error">{errorText(detail.error, "This ticket could not be loaded.")}</Alert>
    );
  }

  const ticket = detail.data;
  const open = ticket.status === "OPEN";
  const actions = canWrite ? statusActions(ticket.nextStatuses) : [];
  const reopen = actions.find((option) => option.to === "OPEN");
  const canSend = open && canWrite && (draft.trim() || files.length) && !reply.isLoading;

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Support ticket #{ticket.id}
              {viewerSide === "OFFICE" && ticket.partnerName ? ` · ${ticket.partnerName}` : ""}
            </p>
            <h2 className="m-0 mt-1 text-lg font-semibold text-slate-900 break-words">
              {ticket.title}
            </h2>
            <p className="m-0 mt-1 text-xs text-slate-500">
              Opened by {ticket.createdByName || "the partner"} · {when(ticket.createdAt)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Chip color={statusColor(ticket.status)} label={statusLabel(ticket.status)} />
            {actions.map((option) => (
              <Button
                key={option.to}
                size="small"
                variant="outlined"
                color={option.color}
                onClick={() => setAction(option)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
        <p className="m-0 mt-4 text-sm text-slate-800 whitespace-pre-wrap break-words">
          {ticket.description}
        </p>
        <AttachmentList attachments={ticket.attachments} />
      </section>

      <TicketStatusHistory history={ticket.statusHistory} />

      <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
        <h3 className="m-0 text-sm font-semibold text-slate-900">Conversation</h3>
        <TicketConversation
          ticketId={ticketId}
          api={api}
          entriesKey={entriesKey}
          viewerSide={viewerSide}
        />

        {canWrite ? (
          <div className="mt-4 border-t border-slate-200 pt-4">
            {!open ? (
              <Alert
                severity={reopen ? "info" : "warning"}
                sx={{ mb: 2 }}
                action={
                  reopen ? (
                    <Button color="inherit" size="small" onClick={() => setAction(reopen)}>
                      Reopen
                    </Button>
                  ) : null
                }
              >
                {reopen
                  ? `This ticket is ${statusLabel(
                      ticket.status
                    ).toLowerCase()}. Reopen it to continue the conversation.`
                  : "This ticket is locked by the office. Only the office can reopen it."}
              </Alert>
            ) : null}
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label={open ? "Write a reply" : "Replies are closed"}
              value={draft}
              disabled={!open || reply.isLoading}
              inputProps={{ maxLength: 8000 }}
              onChange={(event) => setDraft(event.target.value)}
              onPaste={(event) => {
                if (!open || reply.isLoading) return;
                pasteTicketFiles(event, {
                  files,
                  onChange: setFiles,
                  onProblem: (problem) => toast.error(problem),
                });
              }}
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <AttachmentPicker
                files={files}
                onChange={setFiles}
                onProblem={(problem) => toast.error(problem)}
                disabled={!open || reply.isLoading}
              />
              <Button variant="contained" disabled={!canSend} onClick={() => reply.mutate()}>
                {reply.isLoading ? "Sending…" : "Send"}
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <StatusChangeDialog
        action={action}
        busy={changeStatus.isLoading}
        onCancel={() => setAction(null)}
        onConfirm={(note) => changeStatus.mutate({ to: action.to, note })}
      />
    </div>
  );
};

export default SupportTicketThread;
